/** Log message actor markers store staff ids (`|123|`). Legacy logs may still use usernames. */

export type StaffLike = {
  id?: string | number | null
  username?: string | null
  email?: string | null
  permission?: string | number | null
  photo?: string | null
}

export type StaffMaps = {
  byId: Map<string, StaffLike>
  byUsername: Map<string, StaffLike>
}

export const extractLogActorToken = (message: string): string | null => {
  const match = message?.match(/\|([^|]+)\|/);
  return match ? match[1] : null;
};

export const extractAllLogActorTokens = (message: string): string[] => {
  if (!message) return [];
  const tokens: string[] = [];
  const re = /\|([^|]+)\|/g;
  let match = re.exec(message);
  while (match) {
    if (match[1]) tokens.push(match[1]);
    match = re.exec(message);
  }
  return tokens;
};

export const buildStaffMaps = (users: StaffLike[] | null | undefined): StaffMaps => {
  const byId = new Map<string, StaffLike>();
  const byUsername = new Map<string, StaffLike>();
  (users || []).forEach((u) => {
    if (u?.id != null && String(u.id) !== '') {
      byId.set(String(u.id), u);
    }
    if (u?.username) {
      byUsername.set(u.username.replace(/\s+/g, '').toLowerCase(), u);
      byUsername.set(u.username.toLowerCase(), u);
    }
  });
  return { byId, byUsername };
};

export const resolveActorUser = (
  token: string | null | undefined,
  maps: StaffMaps,
): StaffLike | null => {
  if (!token) return null;
  const byId = maps.byId.get(String(token));
  if (byId) return byId;
  const key = token.replace(/\s+/g, '').toLowerCase();
  return maps.byUsername.get(key) || maps.byUsername.get(token.toLowerCase()) || null;
};

export const REMOVED_ACTOR_LABEL = 'Unresolved';

/** Display name for a `|…|` token (id → username; unresolved → Removed). */
export const resolveActorDisplayName = (
  token: string | null | undefined,
  maps: StaffMaps,
  fallback = 'staff',
): string => {
  if (!token) return fallback;
  const user = resolveActorUser(token, maps);
  return user?.username || REMOVED_ACTOR_LABEL;
};

/** Raw token to embed in new log messages (prefer staff id). */
export const logActorToken = (
  staffId: string | number | null | undefined,
  fallbackUsername?: string | null,
  fallback = 'staff',
): string => {
  if (staffId != null && String(staffId) !== '') return String(staffId);
  if (fallbackUsername) return String(fallbackUsername);
  return fallback;
};

export const formatLogActor = (
  staffId: string | number | null | undefined,
  fallbackUsername?: string | null,
  fallback = 'staff',
): string => `|${logActorToken(staffId, fallbackUsername, fallback)}|`;

export type SessionActor = {
  staffId?: string | number | null
  username?: string | null
};

/** Whether a log message mentions this session user (id and/or legacy username). */
export const logMentionsUser = (
  message: string,
  session: SessionActor,
): boolean => {
  const tokens = extractAllLogActorTokens(message);
  if (!tokens.length) return false;
  const staffId = session.staffId != null && String(session.staffId) !== ''
    ? String(session.staffId)
    : '';
  if (staffId && tokens.some((t) => t === staffId)) return true;
  const username = (session.username || '').trim();
  if (username && tokens.some((t) => t.toLowerCase() === username.toLowerCase())) return true;
  return false;
};

/**
 * Actor identity for permission / ownership checks.
 * Prefer `created_by` (staff id), else first `|…|` token in the message.
 */
export const getLogActorIdentity = (
  log: { message?: string; created_by?: string | null },
): string | null => {
  if (log.created_by != null && String(log.created_by) !== '') {
    return String(log.created_by);
  }
  return extractLogActorToken(log.message || '');
};
